import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { CV_LABELS } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import { resolveMagentaHobbyIcon } from './magentaRibbonHobbies';
import './MagentaRibbonTemplate.css';

const extractYear = (date: string): string => {
    const m = String(date || '').match(/(19|20)\d{2}/);
    return m ? m[0] : (date || '').slice(0, 4) || '—';
};

type IconProps = { className?: string };

const svg = (children: React.ReactNode, p: IconProps = {}) => (
    <svg
        className={p.className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

const IconPhone = (p?: IconProps) =>
    svg(<path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a1.5 1.5 0 0 1-1.7 1.5A17 17 0 0 1 3.6 5.2 1.5 1.5 0 0 1 5 3.5z" />, p);

const IconMail = (p?: IconProps) =>
    svg(
        <>
            <rect x="2.5" y="5" width="19" height="14" rx="2" />
            <path d="M3.2 6.4 12 13l8.8-6.6" />
        </>,
        p
    );

const IconGlobe = (p?: IconProps) =>
    svg(
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" />
        </>,
        p
    );

const IconPin = (p?: IconProps) =>
    svg(
        <>
            <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" />
            <circle cx="12" cy="10" r="2.5" />
        </>,
        p
    );

const MagentaRibbonTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const primaryColor = data.style?.primaryColor || '#c11a5b';
    const { fontFamily = '' } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const refContinued = isSectionContinuation(source, 'references', pageIndex);
    const hobbiesContinued = isSectionContinuation(source, 'interests', pageIndex);

    const firstName = data.personalInfo.firstName || (showPlaceholders ? 'TAYA' : '');
    const lastName = data.personalInfo.lastName || (showPlaceholders ? 'MIASS' : '');
    const fullName = `${firstName} ${lastName}`.trim();
    const profession =
        data.personalInfo.profession || (showPlaceholders ? 'Web Designer & Developer' : '');

    const phone = data.personalInfo.phone || (showPlaceholders ? '+001 123 456 789' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'Info@yourmail.com' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'www.youwebsite.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Street Name, Country' : '');
    const photoSrc = data.personalInfo.profileImage || '';

    const summary =
        data.personalInfo.summary ||
        (showPlaceholders
            ? "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat. Ut wisi enim ad minim veniam, quis nostrud exerci tation ullamcorper suscipit lobortis nisl ut aliquip dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s."
            : '');

    const contactLines = [phone, email, website, address].filter(Boolean);

    const educations =
        data.education.length > 0
            ? data.education
            : showPlaceholders
              ? [
                    {
                        id: 'p-edu-1',
                        degree: 'Enter Masters Degree',
                        institution: 'University / College / Institute',
                        field: '',
                        startDate: '2011',
                        endDate: '',
                        isCurrent: false,
                        description: '',
                    },
                    {
                        id: 'p-edu-2',
                        degree: 'Enter Bachelor Degree',
                        institution: 'University / College / Institute',
                        field: '',
                        startDate: '2015',
                        endDate: '',
                        isCurrent: false,
                        description: '',
                    },
                ]
              : [];

    const languages =
        data.languages && data.languages.length > 0
            ? data.languages
            : showPlaceholders
              ? [
                    { name: 'French', level: 92 },
                    { name: 'Chinese', level: 78 },
                    { name: 'Spanish', level: 64 },
                    { name: 'English', level: 88 },
                    { name: 'Hindi', level: 70 },
                ]
              : [];

    const skills =
        data.skills && data.skills.length > 0
            ? data.skills
            : showPlaceholders
              ? [
                    { name: 'Creativity', level: 90 },
                    { name: 'Teamwork', level: 72 },
                    { name: 'Management', level: 80 },
                    { name: 'Leader', level: 62 },
                    { name: 'Communication', level: 86 },
                    { name: 'UI/UX', level: 76 },
                ]
              : [];

    const interests =
        data.interests && data.interests.length > 0
            ? data.interests
            : showPlaceholders
              ? [
                    { name: 'headphones' },
                    { name: 'book' },
                    { name: 'camera' },
                    { name: 'plane' },
                    { name: 'gamepad' },
                    { name: 'bike' },
                    { name: 'palette' },
                    { name: 'music' },
                ]
              : [];

    const experiences =
        data.experience.length > 0
            ? data.experience
            : showPlaceholders
              ? [
                    {
                        id: 'p-exp-1',
                        position: 'Computer Software Developer',
                        company: 'Company Name',
                        startDate: '2015',
                        endDate: '2017',
                        isCurrent: false,
                        description:
                            "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s.",
                    },
                    {
                        id: 'p-exp-2',
                        position: 'Senior UI / UX Design and Developer',
                        company: 'Company Name',
                        startDate: '2015',
                        endDate: '2017',
                        isCurrent: false,
                        description:
                            "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s.",
                    },
                    {
                        id: 'p-exp-3',
                        position: 'Web Design and Developer',
                        company: 'Company Name',
                        startDate: '2015',
                        endDate: '2017',
                        isCurrent: false,
                        description:
                            "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s.",
                    },
                ]
              : [];

    const references =
        data.references && data.references.length > 0
            ? data.references
            : showPlaceholders
              ? [
                    {
                        id: 'p-ref-1',
                        name: 'Reference full Name',
                        position: 'Job Position Goes Here',
                        company: 'Company Name Goes Here',
                        email: 'Info@yourmail.com',
                        phone: '+1 123 456',
                    },
                    {
                        id: 'p-ref-2',
                        name: 'Reference full Name',
                        position: 'Job Position Goes Here',
                        company: 'Company Name Goes Here',
                        email: 'Info@yourmail.com',
                        phone: '+1 123 456',
                    },
                ]
              : [];

    const cssVars = {
        ['--mr-primary' as string]: primaryColor,
    } as React.CSSProperties;

    const hobbyItems =
        interests.length > 0
            ? interests.slice(0, 8)
            : showPlaceholders
              ? [
                    { name: 'headphones' },
                    { name: 'book' },
                    { name: 'camera' },
                    { name: 'plane' },
                    { name: 'gamepad' },
                    { name: 'bike' },
                    { name: 'palette' },
                    { name: 'music' },
                ]
              : [];

    return (
        <div
            className={`shadow-2xl cv-page-sheet mr-page${isContinuationPage ? ' mr-continued' : ''} ${fontFamily}`.trim()}
            style={cssVars}
        >
            <div className="mr-sheet">
                {!isContinuationPage ? (
                    <>
                        <div className="mr-photo">
                            <div className="mr-photo__inner">
                                {photoSrc ? (
                                    <img src={photoSrc} alt={fullName} />
                                ) : showPlaceholders ? (
                                    <>
                                        Your
                                        <br />
                                        Photo
                                    </>
                                ) : null}
                            </div>
                        </div>

                        <header className="mr-identity">
                            {fullName && <h1 className="cv-fs-name">{fullName}</h1>}
                            {profession && <p className="cv-fs-profession">{profession}</p>}
                        </header>

                        {contactLines.length > 0 && (
                            <ul className="cv-fs-contact mr-contact">
                                {contactLines.map((line, i) => (
                                    <li key={i}>{line}</li>
                                ))}
                            </ul>
                        )}

                        <aside className="mr-ribbon" aria-hidden>
                            <IconPhone />
                            <IconMail />
                            <IconGlobe />
                            <IconPin />
                        </aside>

                        <div className="mr-rule-h" />
                        <div className="mr-rule-v" />
                    </>
                ) : (
                    <>
                        <div className="mr-continued-bar" />
                        <div className="mr-continued-head">
                            <p className="cv-fs-name">{fullName}</p>
                            <span>{CV_LABELS.suite}</span>
                        </div>
                        <div className="mr-rule-v" style={{ top: '22mm', height: '250mm' }} />
                    </>
                )}

                <div className="mr-col-left" data-cv-sidebar>
                    {educations.length > 0 && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.education}
                                {eduContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-education mr-edu">
                                {educations.map((item, i) => (
                                    <div
                                        className="mr-edu__item"
                                        key={item.id || i}
                                        data-cv-block
                                        data-cv-item-id={item.id || undefined}
                                    >
                                        <span className="mr-edu__year">{extractYear(item.startDate)}</span>
                                        <span className="mr-edu__marker">
                                            <span />
                                        </span>
                                        <div>
                                            <h3 className="mr-edu__degree">{item.degree}</h3>
                                            {item.institution && (
                                                <p className="mr-edu__school">{item.institution}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {languages.length > 0 && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.languages}
                                {langContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-languages">
                            {languages.map((item, i) => (
                                <div
                                    className="mr-level"
                                    key={item.id || item.name || i}
                                    data-cv-block
                                    data-cv-item-id={item.id || undefined}
                                >
                                    <span className="mr-level__label">{item.name}</span>
                                    <div className="mr-level__track">
                                        <div
                                            className="mr-level__fill"
                                            style={{
                                                width: `${Math.min(100, Math.max(0, item.level ?? 50))}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                            </div>
                        </section>
                    )}

                    {skills.length > 0 && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.skills}
                                {skillsContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-skills">
                            {skills.map((item, i) => (
                                <div
                                    className="mr-level"
                                    key={item.id || item.name || i}
                                    data-cv-block
                                    data-cv-item-id={item.id || undefined}
                                >
                                    <span className="mr-level__label">{item.name}</span>
                                    <div className="mr-level__track">
                                        <div
                                            className="mr-level__fill"
                                            style={{
                                                width: `${Math.min(100, Math.max(0, item.level ?? 50))}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                            </div>
                        </section>
                    )}

                    {(hobbyItems.length > 0 || (showPlaceholders && !isContinuationPage)) && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.interests}
                                {hobbiesContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-interests mr-hobbies__grid" aria-label={CV_LABELS.interests}>
                                {hobbyItems.map((interest, i) => {
                                    const Icon = resolveMagentaHobbyIcon(interest.name, i);
                                    const itemId =
                                        'id' in interest && typeof interest.id === 'string'
                                            ? interest.id
                                            : undefined;
                                    return (
                                        <i
                                            key={itemId ?? `hobby-${i}-${interest.name}`}
                                            title={interest.name || undefined}
                                            aria-label={interest.name || undefined}
                                            data-cv-block
                                            data-cv-item-id={itemId}
                                        >
                                            <Icon />
                                        </i>
                                    );
                                })}
                            </div>
                        </section>
                    )}
                </div>

                <div className="mr-col-right" data-cv-main>
                    {summary && !isContinuationPage && (
                        <section className="mr-section" data-cv-block data-cv-item-id="summary">
                            <h2>{CV_LABELS.profile}</h2>
                            <p className="cv-fs-summary mr-text">{summary}</p>
                        </section>
                    )}

                    {experiences.length > 0 && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.experience}
                                {expContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-experience mr-exp">
                                {experiences.map((item, i) => (
                                    <div
                                        className="mr-exp__item"
                                        key={item.id || i}
                                        data-cv-block
                                        data-cv-item-id={item.id || undefined}
                                    >
                                        <span className="mr-exp__years">
                                            {extractYear(item.startDate)}
                                            <br />
                                            {item.isCurrent ? CV_LABELS.present : extractYear(item.endDate)}
                                        </span>
                                        <span className="mr-exp__marker">
                                            <span />
                                        </span>
                                        <div>
                                            <h3 className="mr-exp__role">{item.position}</h3>
                                            {item.company && (
                                                <p className="mr-exp__company">
                                                    {item.company}
                                                    {showPlaceholders && !data.experience.length ? (
                                                        <span>Institune Name</span>
                                                    ) : null}
                                                </p>
                                            )}
                                            {item.description && (
                                                <p className="mr-text mr-exp__desc">{item.description}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {references.length > 0 && (
                        <section className="mr-section">
                            <h2>
                                {CV_LABELS.references}
                                {refContinued ? ` · ${CV_LABELS.suite}` : ''}
                            </h2>
                            <div className="cv-fs-references mr-ref">
                                {references.map((item, i) => (
                                    <div
                                        key={item.id || i}
                                        data-cv-block
                                        data-cv-item-id={item.id || undefined}
                                    >
                                        <h3 className="mr-ref__name">{item.name}</h3>
                                        {item.position && (
                                            <p className="mr-ref__position">{item.position}</p>
                                        )}
                                        {item.company && (
                                            <p className="mr-ref__company">{item.company}</p>
                                        )}
                                        {item.email && (
                                            <p className="mr-ref__line">Mail : {item.email}</p>
                                        )}
                                        {item.phone && (
                                            <p className="mr-ref__line">Phone : {item.phone}</p>
                                        )}
                                        {showPlaceholders && !data.references?.length && (
                                            <p className="mr-ref__line">Street Name, Country</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MagentaRibbonTemplate;
